import {AutocompleteRenderInputParams, Chip, styled, Typography} from '@mui/material';
import {useCallback, useMemo} from 'react';

import {PolicyFilter} from 'modules/decisionsNext/api/types';

import Autocomplete from 'components/form-next/Autocomplete';
import TextField from 'components/form-next/TextField';
import LoadingIndicator from 'components/LoadingIndicator';

import {useDepartmentLevelOptions} from 'modules/decisionsNext/components/BaseFilter/hooks/useDepartmentLevelOptions';

const SRoot = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SLoadingIndicator = styled(LoadingIndicator)(() => ({
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
}));

const SOptionWrapper = styled('li')(() => ({
  '&&': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const SEndAdornmentChip = styled(Chip)(() => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  right: 30,
}));

type DepartmentLevelBlockProps = {
  departmentLevelFilterStore: Pick<PolicyFilter, 'departmentLevel'>;
  setDepartmentLevelFilterStore: (policyFilter: Pick<PolicyFilter, 'departmentLevel'>) => void;
  isIgnored?: boolean;
  withHelperText?: boolean;
  label?: string;
};

export const DepartmentLevelBlock = ({
  departmentLevelFilterStore,
  setDepartmentLevelFilterStore,
  isIgnored,
  withHelperText = true,
  label = 'Business Org Level shown in charts and tables',
}: DepartmentLevelBlockProps) => {
  const {options, loading} = useDepartmentLevelOptions(isIgnored);

  const option = useMemo(() => {
    if (!departmentLevelFilterStore.departmentLevel) {
      return options.find(option => option.isDefault) ?? null;
    }

    return options.find(option => option.id === departmentLevelFilterStore.departmentLevel) ?? null;
  }, [options, departmentLevelFilterStore]);

  const handleChange = (newValue: (typeof options)[number] | null) => {
    setDepartmentLevelFilterStore({departmentLevel: newValue?.isDefault ? undefined : newValue?.id ?? undefined});
  };

  const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: (typeof options)[number]) => {
    return (
      <SOptionWrapper {...props} key={option.id}>
        <Typography variant='body2'>{option.label}</Typography>

        {option.isDefault && <Chip label='Default' size='small' />}
      </SOptionWrapper>
    );
  }, []);

  const renderInput = useCallback(
    ({InputLabelProps: _, ...params}: AutocompleteRenderInputParams) => {
      const endAdornment = option?.isDefault ? (
        <>
          <SEndAdornmentChip label='Default' size='small' />
          {params.InputProps.endAdornment}
        </>
      ) : (
        params.InputProps.endAdornment
      );

      return (
        <TextField
          {...params}
          label={label}
          onKeyDown={e => e.stopPropagation()}
          autoComplete='off'
          autoCapitalize='none'
          spellCheck='false'
          color='secondary'
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: loading ? <SLoadingIndicator size={16} padding={0} /> : endAdornment,
            },
          }}
        />
      );
    },
    [label, loading, option?.isDefault]
  );

  if (isIgnored) {
    return null;
  }

  return (
    <SRoot>
      <Autocomplete
        loading={loading}
        options={options}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={option}
        onChange={(_, value) => handleChange(value)}
        renderOption={renderOption}
        renderInput={renderInput}
        disableClearable={option?.isDefault}
      />

      {withHelperText && (
        <Typography variant='body3' color='text.secondary'>
          Levels are organized hierarchically from Highest to Lowest
        </Typography>
      )}
    </SRoot>
  );
};
